/**
 * Optimizer Control Center — copy/paste clipboard types
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FlowOptimizerConfig } from '@trafficpulse/shared';

import type { FlowOptimizerProvider } from '@/features/flow-optimizer/services/flow-optimizer-api';

/**
 * The portable optimizer recipe — provider-agnostic fields that can be copied
 * from one flow's config onto another. Excludes target identity (campaignId,
 * flowId), the flow-specific `standardJson` snapshot, run-state (enabled,
 * killSwitch) and run history, so pasting never rewires a target or flips
 * its auto-run / pause state.
 */
export type PortableOptimizerConfig = Pick<
  FlowOptimizerConfig,
  | 'mode'
  | 'intervalMin'
  | 'minWeight'
  | 'maxWeight'
  | 'excludeRules'
  | 'capacityRules'
  | 'formula'
  | 'packageId'
>;

/**
 * Which parts of the recipe a paste/duplicate writes. Unchecked keys are
 * omitted from the upsert body, so the target keeps its current value. `weights`
 * covers the minWeight + maxWeight pair; `excludeRules` carries the capacity
 * rules with it (same Rules tab, half a strategy without them); `package`
 * carries the source's package attribution and is skipped when the source has
 * none (a paste never unassigns).
 */
export type PasteFieldKey =
  | 'mode'
  | 'formula'
  | 'excludeRules'
  | 'interval'
  | 'weights'
  | 'package';

export const ALL_PASTE_FIELD_KEYS: readonly PasteFieldKey[] = [
  'mode',
  'formula',
  'excludeRules',
  'interval',
  'weights',
  'package',
];

/** Default selection for a recipe: everything — minus the package row when
 *  the source carries none (hidden in the checklist, absent from the set). */
export function defaultPasteFields(
  recipe: Pick<PortableOptimizerConfig, 'packageId'>,
): Set<PasteFieldKey> {
  return new Set(
    recipe.packageId
      ? ALL_PASTE_FIELD_KEYS
      : ALL_PASTE_FIELD_KEYS.filter((key) => key !== 'package'),
  );
}

/** A copied config plus where it came from (drives the paste-bar label). */
export interface CopiedOptimizerConfig {
  source: {
    provider: FlowOptimizerProvider;
    campaignId: string;
    flowId: string;
    campaignName: string;
    flowName: string;
  };
  recipe: PortableOptimizerConfig;
}
